/* @fflow */
import React from 'react';
// $FlowIgnore
import { connect } from 'react-redux';

import formatFileSize from 'utils/formatFileSize';

import { setModal } from 'ducks/modal';
import { BiospecimenDownloadContainer } from 'containers/DownloadContainers';
import { DetailsSummaryContainer } from 'containers/DetailsSummaryContainer';
import { HierarchyLinksContainer } from 'containers/HierarchyContainer';

import Row from 'components/UIKit/Flex/Row';
import Section from 'components/UIKit/Details/Content/Section';
import { Spinner } from 'components/UIKit/Spinner';
import Table, { THead, TBody, Tr, Th, Td } from 'components/UIKit/Table';
import BiospecimenDetailsNavigation from './BiospecimenDetailsNavigation';
import { HelpIcon, TrashIcon } from 'components/icons';
import { Tooltip } from 'components/UIKit/Tooltip';
import HierarchyTooltip from 'components/Tooltips/HierarchyTooltip';
import DangerButton from 'components/UIKit/Button/DangerButton';
import DeleteEntity from 'components/DeleteEntity';
import ExternalLink from 'components/Links/ExternalLink';
import DetailsPanel from '../DetailsPanel';
import TransactionList from '../TransactionList';
import AnnotationsList from '../AnnotationsList';

function BiospecimenDetails({
  children, links, dispatch, beingDeleted,
}: Object): React.Element {
  let entity = children.item;
  let headerId = ((children || {}).item || {}).submitter_id;

  if (links?.links?.length) entity._links = links.links;

  let processing = beingDeleted.some(x => x.ids.includes(entity.id));

  return (
    <DetailsPanel
      headerId={headerId}
      details={children}
      NavigationComponent={BiospecimenDetailsNavigation}
    >
      <Section customDataTestID="table-actions" id="actions" header="actions">
        <Row style={{ flexWrap: 'wrap' }}>
          <BiospecimenDownloadContainer customDataTestID={`button-${entity.type}`} >
            {entity.type}
          </BiospecimenDownloadContainer>
          <DangerButton
            customDataTestID="button-delete"
            PrefixComponent={
              processing
                ? <Spinner style={{ width: '15px', height: '15px', marginRight: '0.5rem' }} />
                : <TrashIcon style={{ marginRight: '0.2rem' }} />
            }
            disabled={entity.state === 'submitted' || processing}
            onClick={() => dispatch(setModal({
              Component: () => <DeleteEntity entity={entity} />,
            }))}
          >
            {processing ? 'Processing..' : 'Delete'}
          </DangerButton>
        </Row>
      </Section>
      <Section customDataTestID="table-details" id="details">
        <DetailsSummaryContainer entity={entity} entityType={entity.type} />
      </Section>
      <Section
        customDataTestID="table-hierarchy"
        id="hierarchy"
        header="hierarchy"
        icon={
          <Tooltip Component={<HierarchyTooltip />}>
            <ExternalLink
              arias={{ 'aria-label': 'Link to documentation on Hierarchy' }}
              href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Data_Submission_Process/#hierarchy"
              >
              <HelpIcon />
            </ExternalLink>
          </Tooltip>
        }
      >
        { links.isFetching ? <Spinner /> :
          <HierarchyLinksContainer entity={entity} />
        }
      </Section>
      {entity.type === 'aliquot' &&
        <Section id="data" header="files">
          { (entity.submitted_files || []).length > 0 ? (
            <Table style={{border: '1px solid #e5e5e5'}}>
              <THead>
                <Tr>
                  <Th style={{paddingLeft: '2.5rem'}}>File Name</Th>
                  <Th>Format</Th>
                  <Th>State</Th>
                  <Th>File Size</Th>
                </Tr>
              </THead>
              <TBody>
                {entity.submitted_files.map(f => (
                  <Tr key={f.id} style={{alignItems: 'center'}}>
                    <Td>
                      <Row style={{alignItems: 'center'}}>
                        <i className="material-icons" style={{marginRight: '.5rem', color: 'grey'}}>description</i>
                        {f.file_name}
                      </Row>
                    </Td>
                    <Td>{f.data_format}</Td>
                    <Td style={{textTransform: 'capitalize'}}>{f.file_state}</Td>
                    <Td style={{textTransform: 'capitalize'}}>{formatFileSize(f.file_size)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            ) : (
            'No files submitted'
          )}
        </Section>}
      <Section customDataTestID="table-annotations" id="annotations" header="annotations">
        { (entity.annotations || []).length ? (
          <AnnotationsList>{entity.annotations}</AnnotationsList>
        ) : 'No Annotations submitted'}
      </Section>
      <Section customDataTestID="table-transactions" id="transactions" header="transactions" count={(entity._transaction_logs || []).length}>
        { (entity._transaction_logs || []).length ? (
          <TransactionList>{entity._transaction_logs}</TransactionList>
        ) : 'No transactions'}
      </Section>
    </DetailsPanel>);
}

export default connect(
  state => ({
    links: state.links,
    beingDeleted: state.details.beingDeleted,
  })
)(BiospecimenDetails);
